import { useMemo, useRef, useState } from 'react';
import {
  Download,
  FileText,
  FolderUp,
  LayoutGrid,
  List as ListIcon,
  UploadCloud,
} from 'lucide-react';
import {
  useCreateProjectResource,
  useCreateProjectResourceFolder,
  useDeleteProjectResource,
  useDeleteProjectResourceFolder,
  useProjectResourcesTree,
  useEnsureProjectResourceFolders,
} from '../../hooks/useProjects';
import {
  requestProjectResourceUploadUrl,
  type ProjectDetail,
  type ProjectResourceCategory,
  type TreeFile,
  type TreeFolder,
} from '../../lib/projectsApi';
import { uploadToSignedUrl } from '../../lib/clientsApi';
import { ApiError } from '../../lib/api';
import { usePermissions } from '../../hooks/usePermissions';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { useUploadLimit } from '../../hooks/useUploadLimit';
import { bytesToMb } from '../../lib/fileUpload';
import {
  DIRECTORY_INPUT_PROPS,
  filesFromDataTransfer,
  filesFromInput,
  type PickedTree,
} from '../../lib/folderUpload';
import { useI18n } from '../../contexts/I18nContext';
import { EditWithAIButton } from '../ui/EditWithAIButton';
import { UploadProgressList } from '../ui/UploadProgress';
import { Skeleton } from '../ui/Skeleton';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';
import { FolderView, findFolderByPath } from '../resources/FolderView';
import { FilePreviewFrame, type PreviewNavigation } from '../resources/FilePreviewModal';

// ---------------------------------------------------------------------------
// Resources tab — Document Management System (CLE-48)
//
// Explorer/Finder-style file browser over the project's documents:
//   Resources/Invoices/<year>/<MM-month>/<income|outcome>/<file>, Contracts/,
//   Design/, Other resources/. Two views: a folder browser with breadcrumbs and a
//   flat sortable/filterable invoice table.
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium' }).format(new Date(iso));
}

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency }).format(amount);
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Ciornă',
  SENT: 'Emisă',
  PAID: 'Încasată / Plătită',
  OVERDUE: 'Întârziată',
  CANCELLED: 'Anulată',
};

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'OVERDUE':
      return 'bg-red-100 text-red-800';
    case 'SENT':
      return 'bg-blue-100 text-blue-800';
    case 'CANCELLED':
      return 'bg-gray-200 text-gray-600';
    default:
      return 'bg-amber-100 text-amber-800';
  }
}

function DirectionBadge({ direction }: { direction: 'INCOME' | 'OUTCOME' }) {
  const isIncome = direction === 'INCOME';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isIncome ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
      }`}
    >
      {isIncome ? 'Venit' : 'Cheltuială'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Preview modal (PDF inline + image lightbox + invoice metadata)
// ---------------------------------------------------------------------------

function PreviewModal({
  file,
  onClose,
  nav,
}: {
  file: TreeFile;
  onClose: () => void;
  nav?: PreviewNavigation;
}) {
  const inv = file.invoice;
  return (
    <FilePreviewFrame file={file} onClose={onClose} nav={nav}>
      {inv && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm md:grid-cols-4">
          <MetaField label="Număr factură" value={inv.invoiceNumber} />
          <MetaField label="Data emiterii" value={formatDate(inv.issueDate)} />
          <MetaField label="Net" value={formatMoney(inv.amount, inv.currency)} />
          <MetaField label="TVA" value={formatMoney(inv.taxAmount, inv.currency)} />
          <MetaField label="Total" value={formatMoney(inv.amountTotal, inv.currency)} />
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">Status</dt>
            <dd className="mt-0.5">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(inv.status)}`}
              >
                {STATUS_LABELS[inv.status] ?? inv.status}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">Tip</dt>
            <dd className="mt-0.5">
              <DirectionBadge direction={inv.direction} />
            </dd>
          </div>
          <MetaField
            label={inv.direction === 'INCOME' ? 'Client' : 'Furnizor'}
            value={(inv.direction === 'INCOME' ? inv.client : inv.vendor) ?? '—'}
          />
        </div>
      )}
    </FilePreviewFrame>
  );
}

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-gray-900">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flat invoice list/table view (sortable + filterable)
// ---------------------------------------------------------------------------

type SortKey = 'invoiceNumber' | 'issueDate' | 'amountTotal' | 'status' | 'direction';

function InvoiceListView({
  files,
  onPreview,
}: {
  files: TreeFile[];
  onPreview: (file: TreeFile) => void;
}) {
  const invoiceFiles = useMemo(() => files.filter((f) => f.invoice), [files]);

  const [year, setYear] = useState('');
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('issueDate');
  const [sortAsc, setSortAsc] = useState(false);

  const years = useMemo(() => {
    const set = new Set<string>();
    for (const f of invoiceFiles) {
      if (f.invoice?.issueDate) set.add(String(new Date(f.invoice.issueDate).getUTCFullYear()));
    }
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [invoiceFiles]);

  const rows = useMemo(() => {
    const filtered = invoiceFiles.filter((f) => {
      const inv = f.invoice!;
      if (year && (!inv.issueDate || String(new Date(inv.issueDate).getUTCFullYear()) !== year)) {
        return false;
      }
      if (direction && inv.direction !== direction) return false;
      if (status && inv.status !== status) return false;
      return true;
    });
    const sorted = [...filtered].sort((a, b) => {
      const ai = a.invoice!;
      const bi = b.invoice!;
      let cmp = 0;
      switch (sortKey) {
        case 'invoiceNumber':
          cmp = ai.invoiceNumber.localeCompare(bi.invoiceNumber, 'ro');
          break;
        case 'amountTotal':
          cmp = ai.amountTotal - bi.amountTotal;
          break;
        case 'status':
          cmp = ai.status.localeCompare(bi.status);
          break;
        case 'direction':
          cmp = ai.direction.localeCompare(bi.direction);
          break;
        case 'issueDate':
        default:
          cmp =
            (ai.issueDate ? new Date(ai.issueDate).getTime() : 0) -
            (bi.issueDate ? new Date(bi.issueDate).getTime() : 0);
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [invoiceFiles, year, direction, status, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  }

  function SortHeader({ label, k }: { label: string; k: SortKey }) {
    return (
      <th className="py-2 pr-4">
        <button
          type="button"
          onClick={() => toggleSort(k)}
          className="inline-flex items-center gap-1 font-medium hover:text-gray-900"
        >
          {label}
          {sortKey === k && <span aria-hidden>{sortAsc ? '▲' : '▼'}</span>}
        </button>
      </th>
    );
  }

  return (
    <div>
      {/* Filters. Phones: two equal columns (status + count share the last
          row) instead of three selects of three different widths. */}
      <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
        <Select
          label="Toți anii"
          hideLabel
          clearable
          size="sm"
          value={year}
          onChange={setYear}
          placeholder="Toți anii"
          options={years.map((y) => ({ value: y, label: y }))}
          className="sm:w-32"
        />
        <Select
          label="Venituri + Cheltuieli"
          hideLabel
          clearable
          size="sm"
          value={direction}
          onChange={setDirection}
          placeholder="Venituri + Cheltuieli"
          options={[
            { value: 'INCOME', label: 'Venit' },
            { value: 'OUTCOME', label: 'Cheltuială' },
          ]}
          className="sm:w-48"
        />
        <Select
          label="Toate statusurile"
          hideLabel
          clearable
          size="sm"
          value={status}
          onChange={setStatus}
          placeholder="Toate statusurile"
          options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          className="sm:w-44"
        />
        <span className="text-right text-sm text-gray-500 sm:ml-auto">
          {rows.length} {rows.length === 1 ? 'factură' : 'facturi'}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="mt-6 text-sm text-gray-500">Nicio factură pentru filtrele selectate.</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <SortHeader label="Număr" k="invoiceNumber" />
                <SortHeader label="Dată" k="issueDate" />
                <SortHeader label="Tip" k="direction" />
                <SortHeader label="Sumă" k="amountTotal" />
                <SortHeader label="Status" k="status" />
                <th className="py-2 pr-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((f) => {
                const inv = f.invoice!;
                return (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="py-2 pl-4 pr-4">
                      <button
                        type="button"
                        onClick={() => onPreview(f)}
                        className="inline-flex items-center gap-1.5 font-medium text-blue-600 hover:underline"
                      >
                        <FileText size={14} className="text-red-500" />
                        {inv.invoiceNumber}
                      </button>
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{formatDate(inv.issueDate)}</td>
                    <td className="py-2 pr-4">
                      <DirectionBadge direction={inv.direction} />
                    </td>
                    <td className="py-2 pr-4 text-gray-900">
                      {formatMoney(inv.amountTotal, inv.currency)}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(inv.status)}`}
                      >
                        {STATUS_LABELS[inv.status] ?? inv.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-right">
                      {f.downloadUrl && (
                        <a
                          href={f.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800"
                          aria-label="Descarcă"
                        >
                          <Download size={15} />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

type View = 'browser' | 'list';

// Finder-style navigation: the upload category is derived from the top-level
// folder currently open in the browser (root → "Alte resurse"). Files dropped
// inside the auto-generated Facturi tree also land in OTHER, with a hint.
const CATEGORY_BY_ROOT_SEGMENT: Record<string, ProjectResourceCategory> = {
  contracte: 'CONTRACT',
  design: 'DESIGN',
  alte: 'OTHER',
  facturi: 'OTHER',
};

// Subfolders can be made (and uploaded into) below these buckets only — the
// Facturi tree is derived from the invoices themselves.
const FOLDER_BUCKETS = ['contracte', 'design', 'alte'];

// Mirrors normalizeFolderPath in backend/src/routes/resourcesTree.ts: a bucket
// plus at most 8 levels, each name at most 80 characters.
const MAX_FOLDER_DEPTH = 8;
const MAX_SEGMENT_LENGTH = 80;
const UPLOAD_CONCURRENCY = 3;

export function ResourcesTab({ project }: { project: ProjectDetail }) {
  const { data, isLoading, isError } = useProjectResourcesTree(project.id);
  const { t } = useI18n();
  const { can } = usePermissions();
  const canEditResources = can('projects', 'canEditResources');
  const createMutation = useCreateProjectResource(project.id);
  const deleteMutation = useDeleteProjectResource(project.id);
  const createFolderMutation = useCreateProjectResourceFolder(project.id);
  const deleteFolderMutation = useDeleteProjectResourceFolder(project.id);
  const ensureFoldersMutation = useEnsureProjectResourceFolders(project.id);
  const uploadLimit = useUploadLimit();
  const confirm = useConfirm();
  const [view, setView] = useState<View>('browser');
  // The file on screen plus the files the arrows step through (the open folder).
  const [preview, setPreview] = useState<{ file: TreeFile; siblings: TreeFile[] } | null>(null);
  const [folderPath, setFolderPath] = useState('');
  const bucket = folderPath.split('/')[0];
  const category = CATEGORY_BY_ROOT_SEGMENT[bucket] ?? 'OTHER';
  const inBucket = FOLDER_BUCKETS.includes(bucket);
  // Per-file upload progress, rendered as rows under the dropzone.
  const { uploads, isUploading, run } = useUploadQueue();
  // The folder the running batch is going into, so its placeholders are drawn
  // there and not in whichever folder the person browses to meanwhile.
  const [uploadTarget, setUploadTarget] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const currentFolder = data ? findFolderByPath(data.tree, folderPath) : null;
  // "Design / Mockup-uri / v2" for the dropzone hint.
  const targetLabel = useMemo(() => {
    if (!data || !inBucket) return t(`projects.resources.cat.${category}`);
    const names: string[] = [];
    const segments = folderPath.split('/');
    for (let i = 1; i <= segments.length; i += 1) {
      const folder = findFolderByPath(data.tree, segments.slice(0, i).join('/'));
      if (folder) names.push(folder.name);
    }
    return names.join(' / ');
  }, [data, inBucket, folderPath, category, t]);

  async function uploadOne(
    file: File,
    target: { category: ProjectResourceCategory; folderPath?: string },
  ) {
    // The progress row stays up through the whole flow — transfer, then the
    // server writing to storage, then saving the resource row — so it only
    // disappears once the file is genuinely in the folder below.
    await run(file, async (onProgress) => {
      const contentType = file.type || 'application/octet-stream';
      const { uploadUrl, key } = await requestProjectResourceUploadUrl(project.id, {
        fileName: file.name,
        category: target.category,
        contentType,
        fileSize: file.size,
        folderPath: target.folderPath,
      });
      await uploadToSignedUrl(uploadUrl, file, contentType, onProgress);
      await createMutation.mutateAsync({
        name: file.name,
        category: target.category,
        s3Key: key,
        fileType: (file.name.split('.').pop() ?? '').toUpperCase() || undefined,
        fileSize: file.size,
        folderPath: target.folderPath,
      });
    });
  }

  // Several files at once — or whole folders, rebuilt as subfolders: a few
  // transfers in parallel, each with its own progress row; one failing does
  // not stop the others.
  async function handlePicked(picked: PickedTree) {
    if (inputRef.current) inputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
    if (picked.files.length === 0) return;
    setUploadError(null);

    // Folders can only live under Contracte / Design / Alte resurse; from the
    // root or from Facturi, a dropped folder goes under Alte resurse.
    const base = inBucket ? folderPath : 'alte';
    const baseCategory = CATEGORY_BY_ROOT_SEGMENT[base.split('/')[0]] ?? 'OTHER';
    const resolveDir = (dir: string): string => {
      const segments = [
        ...base.split('/'),
        ...dir
          .split('/')
          .map((seg) => seg.trim().replace(/\s+/g, ' ').slice(0, MAX_SEGMENT_LENGTH).trim())
          .filter((seg) => seg && seg !== '.' && seg !== '..'),
      ];
      // Deeper than the server allows: the rest lands in the deepest level.
      return segments.slice(0, MAX_FOLDER_DEPTH + 1).join('/');
    };

    const tooLarge = picked.files.filter((f) => f.file.size > uploadLimit);
    const queue = picked.files
      .filter((f) => f.file.size <= uploadLimit)
      .map(({ file, dir }) => {
        const target = dir ? resolveDir(dir) : base;
        return {
          file,
          target: {
            category: baseCategory,
            folderPath: target.includes('/') ? target : undefined,
          },
        };
      });
    setUploadTarget(folderPath);

    const messages: string[] = [];
    if (picked.dirs.length > 0) {
      try {
        await ensureFoldersMutation.mutateAsync([...new Set(picked.dirs.map(resolveDir))]);
      } catch {
        setUploadError(t('projects.resources.folderCreateFailed'));
        return;
      }
    }

    const failed: string[] = [];
    let lastError: string | null = null;
    async function worker() {
      for (let item = queue.shift(); item; item = queue.shift()) {
        try {
          await uploadOne(item.file, item.target);
        } catch (err) {
          failed.push(item.file.name);
          lastError = err instanceof Error ? err.message : null;
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, worker));

    if (tooLarge.length > 0) {
      messages.push(
        t('projects.resources.tooLarge', {
          mb: bytesToMb(uploadLimit),
          names: tooLarge.map((f) => f.file.name).join(', '),
        }),
      );
    }
    if (failed.length === 1 && picked.files.length === 1) {
      messages.push(lastError ?? t('projects.resources.uploadFailed'));
    } else if (failed.length > 0) {
      messages.push(t('projects.resources.someFailed', { names: failed.join(', ') }));
    }
    if (messages.length > 0) setUploadError(messages.join(' '));
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    // Taken synchronously: the browser empties dataTransfer after the event.
    void filesFromDataTransfer(e.dataTransfer).then(handlePicked);
  }

  async function handleCreateFolder(name: string) {
    try {
      await createFolderMutation.mutateAsync(`${folderPath}/${name}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'FOLDER_EXISTS') {
        throw new Error(t('projects.resources.folderExists'));
      }
      throw new Error(t('projects.resources.folderCreateFailed'));
    }
  }

  async function handleDeleteFolder(folder: TreeFolder) {
    if (
      !(await confirm({
        message: t('projects.resources.deleteFolderConfirm', { name: folder.name }),
        danger: true,
      }))
    )
      return;
    deleteFolderMutation.mutate(folder.path);
  }

  function openPreview(file: TreeFile) {
    const siblings =
      view === 'browser' && currentFolder
        ? currentFolder.children.filter((n): n is TreeFile => n.type === 'file')
        : [];
    setPreview({ file, siblings });
  }

  const previewNav = useMemo((): PreviewNavigation | undefined => {
    if (!preview || preview.siblings.length < 2) return undefined;
    const i = preview.siblings.findIndex((f) => f.id === preview.file.id);
    if (i < 0) return undefined;
    const go = (j: number) => () => setPreview({ ...preview, file: preview.siblings[j] });
    return {
      onPrev: i > 0 ? go(i - 1) : undefined,
      onNext: i < preview.siblings.length - 1 ? go(i + 1) : undefined,
      position: `${i + 1} / ${preview.siblings.length}`,
    };
  }, [preview]);

  async function handleDelete(file: TreeFile) {
    if (
      !(await confirm({
        message: t('projects.resources.deleteConfirm', { name: file.name }),
        danger: true,
      }))
    )
      return;
    deleteMutation.mutate(file.id);
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-5" width={220} />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return <p className="text-sm text-red-600">Încărcarea resurselor a eșuat.</p>;
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
          <button
            type="button"
            onClick={() => setView('browser')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${
              view === 'browser'
                ? 'bg-brand-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <LayoutGrid size={15} /> Foldere
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            className={`inline-flex items-center gap-1.5 border-l border-gray-300 px-3 py-1.5 text-sm font-medium ${
              view === 'list'
                ? 'bg-brand-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ListIcon size={15} /> Listă facturi
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canEditResources && (
            <button
              type="button"
              onClick={() => folderInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <FolderUp size={15} /> {t('resources.uploadFolder')}
            </button>
          )}
          <EditWithAIButton
            variant="secondary"
            label="Încarcă prin AI"
            prompt={`Vreau să încarc un document (factură sau contract) pentru proiectul „${project.name}”. Te rog cere-mi fișierul PDF și salvează-l în structura corectă.`}
          />
        </div>
      </div>

      {/* Manual upload (drag & drop or click), mirroring the client resources tab */}
      {canEditResources && (
        <div className="mb-4 space-y-3">
          <UploadProgressList uploads={uploads} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            disabled={isUploading}
            className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 text-center transition-colors disabled:opacity-50 ${
              dragActive
                ? 'border-brand-500 bg-brand-50'
                : 'border-gray-300 bg-gray-50 hover:border-brand-400'
            }`}
          >
            <UploadCloud className="h-7 w-7 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              {t('projects.resources.dropHint')}
            </span>
            <span className="text-xs text-gray-400">
              {t('projects.resources.savedIn', { folder: targetLabel })} ·{' '}
              {t('resources.maxPerFile', { mb: bytesToMb(uploadLimit) })}
            </span>
            {folderPath.startsWith('facturi') && (
              <span className="text-xs text-amber-600">
                {t('projects.resources.invoicesDerived')}
              </span>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void handlePicked(filesFromInput(e.target.files));
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            {...DIRECTORY_INPUT_PROPS}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void handlePicked(filesFromInput(e.target.files));
            }}
          />
          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
        </div>
      )}

      {deleteMutation.isError && (
        <p className="mb-3 text-sm text-red-600">{t('projects.resources.deleteFailed')}</p>
      )}
      {deleteFolderMutation.isError && (
        <p className="mb-3 text-sm text-red-600">{t('projects.resources.deleteFolderFailed')}</p>
      )}

      {view === 'browser' ? (
        <>
          {data.tree.fileCount === 0 && !isUploading && (
            <p className="mb-3 text-sm text-gray-500">
              Încă niciun document. Încarcă un fișier mai sus (sau prin AI Chat) și va apărea în
              folderul deschis.
            </p>
          )}
          <FolderView
            root={data.tree}
            path={folderPath}
            onPathChange={setFolderPath}
            onPreview={openPreview}
            onDelete={canEditResources ? handleDelete : undefined}
            uploads={uploadTarget === folderPath ? uploads : []}
            onCreateFolder={canEditResources && inBucket ? handleCreateFolder : undefined}
            onDeleteFolder={canEditResources ? handleDeleteFolder : undefined}
          />
        </>
      ) : (
        <InvoiceListView files={data.files} onPreview={openPreview} />
      )}

      {preview && (
        <PreviewModal file={preview.file} nav={previewNav} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
